import { motion } from 'framer-motion'
import { LCARSPanel, LCARSButton, LCARSBar } from '@enterpress/holomatrix'
import { Rocket, Bot, Server, Sparkles, Calendar } from 'lucide-react'
import { trackCTAClick } from '../lib/analytics'
import { useTranslation } from '../i18n/index'

/**
 * Preview Section - Public Preview CTA
 * 
 * Dedicated section for the upcoming Q1 2026 public preview launch
 * Positioned before footer as a prominent call-to-action
 */
export function Preview() {
  const { t } = useTranslation()
  const scrollToWaitlist = () => {
    trackCTAClick('Request Bridge Access', 'Preview')
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })
  }

  const previewFeatures = [
    { icon: Bot, label: t('preview.feature1'), description: t('preview.feature1Desc') },
    { icon: Server, label: t('preview.feature2'), description: t('preview.feature2Desc') },
    { icon: Sparkles, label: t('preview.feature3'), description: t('preview.feature3Desc') },
  ]

  return (
    <section id="preview" className="py-24 px-6 relative">
      {/* Background accent - more prominent */}
      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(255,153,0,0.03)] via-[rgba(255,153,0,0.06)] to-[rgba(255,153,0,0.03)] pointer-events-none" />
      
      {/* Animated border glow */}
      <motion.div
        className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,153,0,0.5)] to-transparent"
        animate={{ opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }}
      />

      <div className="max-w-4xl mx-auto relative">
        {/* Incoming transmission badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(255,153,0,0.12)] border border-[rgba(255,153,0,0.4)]">
            <motion.div 
              className="w-2 h-2 rounded-full bg-[#FF9900]"
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <span className="text-xs font-bold tracking-[2px] uppercase text-[#FF9900]">
              {t('preview.badge')}
            </span>
          </div>
        </motion.div>

        {/* Main content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <LCARSPanel title={t('preview.title')} color="amber" className="overflow-hidden">
            <div className="p-8 md:p-10">
              {/* Header */}
              <div className="text-center mb-10">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Calendar size={24} className="text-[#FFB347]" />
                  <h3 className="text-3xl md:text-4xl font-bold text-white">
                    {t('preview.subtitle')}
                  </h3>
                </div>
                <p className="text-lg text-[rgba(232,244,248,0.7)] max-w-xl mx-auto">
                  {t('preview.description')}
                </p>
              </div>

              {/* Preview features */}
              <div className="grid md:grid-cols-3 gap-4 mb-10">
                {previewFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-4 rounded-xl bg-[rgba(255,179,71,0.08)] border border-[rgba(255,179,71,0.2)] text-center"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-[rgba(255,179,71,0.15)] flex items-center justify-center">
                      <feature.icon size={20} className="text-[#FFB347]" />
                    </div>
                    <div className="text-sm font-semibold text-white mb-1">{feature.label}</div>
                    <div className="text-xs text-[rgba(232,244,248,0.5)]">{feature.description}</div>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <div className="text-center">
                <LCARSButton 
                  color="amber" 
                  size="lg" 
                  onClick={scrollToWaitlist}
                  className="min-w-[250px]"
                >
                  <Rocket size={18} />
                  {t('preview.ctaRequest')}
                </LCARSButton>
                <p className="mt-4 text-xs text-[rgba(232,244,248,0.4)]">
                  {t('preview.ctaWaitlist')}
                </p>
              </div>
            </div>
          </LCARSPanel>
        </motion.div>

        {/* Bottom accent */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 flex items-center justify-center gap-4"
        >
          <LCARSBar color="amber" size="sm" className="w-16" />
          <span className="text-xs text-[rgba(255,179,71,0.6)] font-mono tracking-wider">
            {t('preview.priorityTransmission')}
          </span>
          <LCARSBar color="amber" size="sm" className="w-16" />
        </motion.div>
      </div>
    </section>
  )
}

